// ============================================================
// MyGene — DataTable
// Opinionated wrapper around Ant Design Table that enforces
// every table rule from DESIGN_SYSTEM.md in one place.
//
// Usage (replaces raw <Table> on every page):
//
//   <DataTable
//     columns={columns}
//     dataSource={patients}
//     rowKey="id"
//     loading={loading}
//     onRowClick={p => openModal(p)}
//     onChange={handleTableChange}  // Receives pagination, filters, and sorter
//   />
//
// Filtering & Searching:
// Define filters directly on columns using filterDropdown, filters, onFilter.
// All table changes (pagination, filters, sorting) come through onChange callback.
// For backward compatibility, onSort + onPageChange still work independently.
//
// With search/filter/actions — use <PageToolbar> above the DataTable, not props on the table itself.
// ============================================================

import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Table, Typography } from "antd";
import type { ColumnsType, TableProps, TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import EmptyState from "./EmptyState";
import InlineLoading from "./InlineLoading";
import  SectionCard from "./SectionCard";
import { loadUserPreferences } from "../../utils/userPrefs/UserPrefs";

const { Text } = Typography;

// ── Types ──────────────────────────────────────────────────────

interface SortState {
  field: string;
  order: "ascend" | "descend" | undefined;
}

interface DataTableProps<T extends object> {
  // ── Data
  dataSource: T[];
  columns: ColumnsType<T>;
  rowKey: keyof T | ((r: T) => string | number);
  loading?: boolean;

  // ── Card wrapper
  /** Renders the table inside a SectionCard with this heading */
  title?: React.ReactNode;
  /** Subtitle under the card title */
  subtitle?: string;
  /** Remove the SectionCard wrapper entirely */
  bare?: boolean;

  // ── Interaction
  /** Makes rows clickable; fires on row click */
  onRowClick?: (record: T) => void;
  /** Row selection config — pass selectedRows + setSelectedRows for checkbox selection */
  selectedRows?: T[];
  setSelectedRows?: React.Dispatch<React.SetStateAction<T[]>>;

  // ── Table changes (pagination, sorting, filtering)
  /** Comprehensive change handler — fires for ALL table changes (pagination, sorter, filters) */
  onChange?: (pagination: any, filters: any, sorter: any) => void;

  // ── Pagination
  /** Pass `false` to hide pagination */
  pagination?: false | TablePaginationConfig;
  /** Server-side total (for controlled pagination) */
  total?: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;

  // ── Sorting (controlled)
  sortField?: string;
  sortOrder?: "ascend" | "descend" | undefined;
  onSort?: (field: string, order: "ascend" | "descend" | undefined) => void;

  // ── Misc
  /** Min-height of the loading/empty state area */
  minHeight?: number;
  /** Empty state title */
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: { label: string; onClick: () => void };
  /** Horizontal scroll. Defaults to "max-content" */
  scrollX?: number | string;
  /** Vertical scroll */
  scrollY?: number | string;
  /**
   * Makes the table body fill the remaining viewport height.
   * Pagination stays anchored to the bottom. Use on full-page tables.
   */
  fillHeight?: boolean;
  /** Extra px to subtract when fillHeight is true (e.g. for page-specific banners). Default 0. */
  fillHeightOffset?: number;

  /** Per-row selection controls — e.g. disabling rows that must not be selectable */
  getCheckboxProps?: (record: T) => { disabled?: boolean; name?: string };

  /** Apply a CSS class to individual rows based on record data */
  rowClassName?: (record: T) => string;

  /** Expandable rows config (forwarded to Ant Table) */
  expandable?: TableProps<T>["expandable"];

  style?: React.CSSProperties;
  className?: string;
  /** Optional e2e test anchor placed on the outermost container div. */
  "data-testid"?: string;
}

// ── Component ──────────────────────────────────────────────────

function DataTable<T extends object>({
  dataSource,
  columns,
  rowKey,
  loading = false,

  title,
  subtitle,
  bare = false,

  onRowClick,
  selectedRows,
  setSelectedRows,

  onChange,
  pagination,
  total,
  currentPage,
  pageSize,
  onPageChange,

  sortField,
  sortOrder,
  onSort,

  getCheckboxProps,

  minHeight = 280,
  emptyTitle = "No records found",
  emptyDescription,
  emptyAction,
  scrollX = "max-content",
  scrollY,
  fillHeight = false,
  fillHeightOffset = 0,

  rowClassName,

  expandable,

  style,
  className,
  "data-testid": dataTestId,
}: DataTableProps<T>) {
  const prefs = loadUserPreferences();
  const defaultPageSize = pageSize ?? prefs.tableRowsPerPage ?? 15;

  // ── Fill-height: measure available scroll space ───────────────
  const containerRef = useRef<HTMLDivElement>(null);
  const [computedScrollY, setComputedScrollY] = useState<number | undefined>(undefined);

  const updateScrollY = useCallback(() => {
    if (!fillHeight || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    // Account for: toolbar (~53px), pagination (~56px), card border (2px)
    const reserved = 53 + 56 + 2 + fillHeightOffset;
    const available = window.innerHeight - rect.top - reserved;
    setComputedScrollY(Math.max(available, 120));
  }, [fillHeight, fillHeightOffset]);

  useEffect(() => {
    if (!fillHeight) return;
    updateScrollY();
    const ro = new ResizeObserver(updateScrollY);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("resize", updateScrollY);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateScrollY);
    };
  }, [fillHeight, updateScrollY]);

  const resolvedScrollY = fillHeight ? computedScrollY : scrollY;

  // ── Table change handler ──────────────────────────────────────
  // Passes pagination, filters, and sorter to onChange callback.
  // For backward compatibility with onSort, also extracts sort info separately.
  const handleTableChange: TableProps<T>["onChange"] = (pag, filt, sort) => {
    // Call comprehensive onChange first (if provided)
    if (onChange) {
      onChange(pag, filt, sort);
    }

    // Extract sort info for backward-compatible onSort callback
    if (onSort) {
      const s = sort as SorterResult<T>;
      if (s.field) {
        onSort(
          Array.isArray(s.field) ? s.field.join(".") : String(s.field),
          s.order as "ascend" | "descend" | undefined
        );
      }
    }

    // Extract pagination for backward-compatible onPageChange callback
    if (onPageChange && pag?.current && pag?.pageSize) {
      onPageChange(pag.current, pag.pageSize);
    }
  };

  // ── Columns: inject sort state for controlled mode ────────────
  const resolvedColumns: ColumnsType<T> = useMemo(() => {
    if (!onSort || !sortField) return columns;
    return columns.map(col => {
      const c = col as any;
      if (c.sorter && c.dataIndex === sortField) {
        return { ...col, sortOrder };
      }
      return col;
    });
  }, [columns, onSort, sortField, sortOrder]);

  // ── Row selection ─────────────────────────────────────────────
  const rowSelection = selectedRows && setSelectedRows
    ? {
        selectedRowKeys: selectedRows.map(r =>
          typeof rowKey === "function" ? rowKey(r) : (r as any)[rowKey]
        ),
        onChange: (_keys: React.Key[], rows: T[]) => setSelectedRows(rows),
        ...(getCheckboxProps ? { getCheckboxProps } : {}),
      }
    : undefined;

  // ── Pagination config ─────────────────────────────────────────
  const paginationConfig: TablePaginationConfig | false =
    pagination === false
      ? false
      : {
          current:  currentPage,
          pageSize: defaultPageSize,
          total,
          showSizeChanger: false,
          showTotal: (t, range) => (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {range[0]}–{range[1]} of {t}
            </Text>
          ),
          onChange: onPageChange,
          ...(typeof pagination === "object" ? pagination : {}),
        };

  // ── Table element ─────────────────────────────────────────────
  const tableEl = loading ? (
    <InlineLoading minHeight={minHeight} />
  ) : (
    <Table<T>
      dataSource={dataSource}
      columns={resolvedColumns}
      rowKey={rowKey as any}
      size="small"
      pagination={paginationConfig}
      rowSelection={rowSelection}
      onChange={handleTableChange}
      scroll={{ x: scrollX, y: resolvedScrollY }}
      locale={{
        emptyText: (
          <EmptyState
            title={emptyTitle}
            description={emptyDescription}
            action={emptyAction}
          />
        ),
      }}
      rowClassName={rowClassName}
      expandable={expandable}
      onRow={onRowClick
        ? record => ({
            onClick: () => onRowClick(record),
            style: { cursor: "pointer" },
          })
        : undefined
      }
    />
  );

  // ── Bare mode (no card wrapper) ───────────────────────────────
  if (bare) {
    return (
      <div ref={containerRef} className={className} style={style} data-testid={dataTestId}>
        {tableEl}
      </div>
    );
  }

  // ── Card-wrapped mode ──────────────────────────────────
	return (
		<div ref={containerRef} data-testid={dataTestId}>
			{tableEl}
		</div>
  );
}

export default DataTable;